/**
 * Phase 7B+7C+7F — Staging Rail (Right Panel)
 *
 * Pure staging layer: shows bucket cards with counts, draft/completed state,
 * and clickable entry points. No large forms, no clutter.
 *
 * 7C: Richer summary text, status indicators
 * 7F: Progress bar, all buckets actionable, polished summaries
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import {
  Code2, Pill, TestTubes, ListChecks, ShieldCheck,
  ChevronRight, ArrowRight, Check, Clock, AlertCircle,
  Sparkles, X, ShieldAlert
} from 'lucide-react';
import MiniPanelPrescriptions from './MiniPanelPrescriptions';
import MiniPanelLabs from './MiniPanelLabs';
import { api } from '../../services/api';
import { QualityChipStrip } from '../quality/QualityChipStrip';
import { QualityChipStripDrawer } from '../quality/QualityChipStripDrawer';

const BUCKETS = [
  { key: 'coding',        label: 'Coding',         icon: Code2,       mini: false },
  { key: 'prescriptions', label: 'Prescriptions',  icon: Pill,        mini: true  },
  { key: 'labs',          label: 'Labs',            icon: TestTubes,   mini: true  },
  { key: 'actions',       label: 'Actions',         icon: ListChecks,  mini: false },
  { key: 'aco',           label: 'ACO / Quality',   icon: ShieldCheck, mini: false },
];


export default function StagingRail({
  enc, outputs, encounterId, fetchEncounter, setEncounter,
  qualityOverview, qualityLoading, onQualityRefresh,
  expandedMed, setExpandedMed, medEdits, setMedEdits,
}) {
  const navigate = useNavigate();
  const [openPanel, setOpenPanel] = useState(null);
  const [safetyAlerts, setSafetyAlerts] = useState({ patient_allergies: [], medications: {} });
  const [drawerChip, setDrawerChip] = useState(null); // quality chip drawer
  const [chipSummary, setChipSummary] = useState(null); // from chip strip

  // Load safety data whenever medications change — runs in background, never blocks
  const meds = (outputs?.chart_updates || []).filter(cu => cu?.update_type?.startsWith('medication'));
  useEffect(() => {
    if (!encounterId || meds.length === 0) return;
    api.get(`/encounters/${encounterId}/medication-safety`)
      .then(r => setSafetyAlerts(r.data || { patient_allergies: [], medications: {} }))
      .catch(() => {}); // Never break the visit on safety check failure
  }, [encounterId, meds.length]); // eslint-disable-line

  // Summarise safety flags for the prescriptions bucket
  const allSafetyFlags = Object.values(safetyAlerts.medications || {});
  const allergyCount = allSafetyFlags.filter(f => f.allergy_alert).length;
  const contraCount = allSafetyFlags.filter(f => (f.contraindications || []).length > 0).length;
  const hasSafetyIssue = allergyCount > 0 || contraCount > 0;

  const bucketData = extractBucketData(enc, outputs, qualityOverview);
  const completedCount = BUCKETS.filter(b => bucketData[b.key]?.status === 'complete').length;
  const activeBucketCount = BUCKETS.filter(b => (bucketData[b.key]?.count || 0) > 0).length;
  const progressPct = activeBucketCount > 0 ? Math.round((completedCount / activeBucketCount) * 100) : 0;

  const handleBucketClick = (key) => {
    const bucket = BUCKETS.find(b => b.key === key);
    if (bucket?.mini) {
      setOpenPanel(openPanel === key ? null : key);
    } else {
      navigate(`/finalize/${encounterId}`);
    }
  };

  const closeMiniPanel = () => setOpenPanel(null);

  return (
    <ScrollArea className="h-full">
      <div className="p-3 space-y-2" data-testid="staging-rail">
        {/* Header with progress */}
        <div className="mb-1">
          <div className="flex items-center justify-between mb-1.5">
            <h3 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
              Staging
            </h3>
            {enc?.status !== 'finalized' && (
              <Badge variant="outline" className={`text-[9px] h-4 ${bucketData._totalPending === 0 && bucketData._allLinked ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : ''}`}>
                {bucketData._totalPending > 0
                  ? `${bucketData._totalPending} pending`
                  : bucketData._allLinked
                    ? 'Ready'
                    : 'Unlinked items'}
              </Badge>
            )}
          </div>
          {/* Progress bar */}
          {activeBucketCount > 0 && enc?.status !== 'finalized' && (
            <div className="h-1 rounded-full bg-muted overflow-hidden" data-testid="staging-progress-bar">
              <div
                className="h-full rounded-full transition-all duration-500 ease-out"
                style={{
                  width: `${progressPct}%`,
                  backgroundColor: progressPct === 100 ? 'hsl(var(--chart-2))' : 'hsl(var(--primary))',
                }}
              />
            </div>
          )}
        </div>

        {/* Bucket Cards */}
        {/* Safety alert banner — shown at top when allergy/contraindication detected */}
        {hasSafetyIssue && meds.length > 0 && (
          <div className={`rounded-lg border px-3 py-2 mb-1 ${allergyCount > 0 ? 'bg-red-50 border-red-300' : 'bg-amber-50 border-amber-300'}`}
            data-testid="staging-safety-banner">
            <div className="flex items-center gap-1.5">
              <ShieldAlert className={`w-3.5 h-3.5 shrink-0 ${allergyCount > 0 ? 'text-red-500' : 'text-amber-500'}`} />
              <p className={`text-[10px] font-bold uppercase tracking-wider ${allergyCount > 0 ? 'text-red-700' : 'text-amber-700'}`}>
                {allergyCount > 0 ? 'Allergy Alert' : 'Drug Caution'}
              </p>
            </div>
            {/* Allergy details */}
            {allergyCount > 0 && (
              <div className="mt-1 space-y-0.5">
                {Object.entries(safetyAlerts.medications || {})
                  .filter(([, v]) => v.allergy_alert)
                  .map(([medId, flags]) => {
                    const medCu = meds.find(m => m.id === medId);
                    const alert = flags.allergy_alert;
                    return (
                      <p key={medId} className="text-[10px] text-red-700 leading-snug">
                        <span className="font-semibold">{medCu?.medication_name || 'Medication'}</span>
                        {' — '}
                        {alert.type === 'allergy_exact' ? `allergy to ${alert.allergen}` : alert.message.replace(/^(ALLERGY.*?:)/i, '').trim()}
                      </p>
                    );
                  })
                }
              </div>
            )}
            {/* Contraindication details */}
            {contraCount > 0 && allergyCount === 0 && (
              <div className="mt-1 space-y-0.5">
                {Object.entries(safetyAlerts.medications || {})
                  .filter(([, v]) => (v.contraindications || []).length > 0)
                  .map(([medId, flags]) => {
                    const medCu = meds.find(m => m.id === medId);
                    return (flags.contraindications || []).map((c, ci) => (
                      <p key={`${medId}-${ci}`} className="text-[10px] text-amber-800 leading-snug">
                        <span className="font-semibold">{medCu?.medication_name || 'Med'}</span>
                        {' + '}
                        <span>{c.condition}</span>
                      </p>
                    ));
                  })
                }
              </div>
            )}
            <button
              onClick={() => { setOpenPanel(null); navigate(`/finalize/${encounterId}`); }}
              className={`text-[9px] mt-1.5 font-medium ${allergyCount > 0 ? 'text-red-600 hover:text-red-800' : 'text-amber-700 hover:text-amber-900'}`}>
              Review in Prescriptions →
            </button>
          </div>
        )}

        {BUCKETS.map(bucket => {
          const data = bucketData[bucket.key] || {};
          const isOpen = openPanel === bucket.key;
          const Icon = bucket.icon;
          const hasItems = (data.count || 0) > 0;
          const isComplete = data.status === 'complete';

          // Safety indicators — only on Prescriptions bucket
          const isPrescriptions = bucket.key === 'prescriptions';
          const bucketHasSafetyIssue = isPrescriptions && hasSafetyIssue;
          const bucketAllergyCount = isPrescriptions ? allergyCount : 0;
          const bucketContraCount = isPrescriptions ? contraCount : 0;

          return (
            <div key={bucket.key}>
              <button
                onClick={() => handleBucketClick(bucket.key)}
                className={`
                  w-full text-left rounded-lg border px-3 py-2.5 transition-all duration-150
                  ${isOpen
                    ? 'border-primary/30 bg-primary/5 shadow-sm'
                    : bucketHasSafetyIssue && !isComplete
                      ? bucketAllergyCount > 0
                        ? 'border-red-300 bg-red-50/30 hover:bg-red-50/50 shadow-sm'
                        : 'border-amber-300 bg-amber-50/20 hover:bg-amber-50/40'
                      : isComplete
                        ? 'border-emerald-200/60 bg-emerald-50/30 hover:bg-emerald-50/50'
                        : hasItems
                          ? 'border-border hover:border-foreground/20 hover:shadow-sm bg-card'
                          : 'border-border/50 bg-muted/20 opacity-50 hover:opacity-70'
                  }
                `}
                data-testid={`staging-bucket-${bucket.key}`}
              >
                <div className="flex items-center gap-2.5">
                  {/* Icon — with pulsing red dot overlay for allergy alerts */}
                  <div className="relative">
                    <div className={`p-1 rounded-md ${
                      bucketAllergyCount > 0 && !isComplete ? 'bg-red-100'
                      : bucketContraCount > 0 && !isComplete ? 'bg-amber-100'
                      : isComplete ? 'bg-emerald-100' : hasItems ? 'bg-muted' : 'bg-muted/50'
                    }`}>
                      <Icon className={`w-3.5 h-3.5 ${
                        bucketAllergyCount > 0 && !isComplete ? 'text-red-600'
                        : bucketContraCount > 0 && !isComplete ? 'text-amber-600'
                        : isComplete ? 'text-emerald-600' : 'text-muted-foreground'
                      }`} />
                    </div>
                    {/* Pulsing alert dot */}
                    {bucketHasSafetyIssue && !isComplete && (
                      <span className={`absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full ${
                        bucketAllergyCount > 0 ? 'bg-red-500' : 'bg-amber-500'
                      } animate-pulse`} />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-medium text-foreground">{bucket.label}</span>
                      {hasItems && (
                        <span className="text-[10px] text-muted-foreground tabular-nums">{data.count}</span>
                      )}
                    </div>
                    {/* Safety summary line — shown instead of/alongside normal summary */}
                    {bucketHasSafetyIssue && !isComplete ? (
                      <div className="mt-0.5 space-y-0.5">
                        {bucketAllergyCount > 0 && (
                          <p className="text-[10px] text-red-600 font-medium truncate flex items-center gap-1">
                            <AlertCircle className="w-2.5 h-2.5 shrink-0" />
                            {bucketAllergyCount === 1
                              ? (() => {
                                  const flagged = allSafetyFlags.find(f => f.allergy_alert);
                                  const medEntry = Object.entries(safetyAlerts.medications || {}).find(([, v]) => v.allergy_alert);
                                  const medCu = meds.find(m => m.id === medEntry?.[0]);
                                  return `${medCu?.medication_name || 'Medication'}: ${flagged?.allergy_alert?.allergen || 'allergy'} conflict`;
                                })()
                              : `${bucketAllergyCount} allergy conflict${bucketAllergyCount > 1 ? 's' : ''}`
                            }
                          </p>
                        )}
                        {bucketContraCount > 0 && (
                          <p className="text-[10px] text-amber-700 truncate flex items-center gap-1">
                            <AlertCircle className="w-2.5 h-2.5 shrink-0" />
                            {bucketContraCount === 1
                              ? (() => {
                                  const medEntry = Object.entries(safetyAlerts.medications || {}).find(([, v]) => (v.contraindications || []).length > 0);
                                  const medCu = meds.find(m => m.id === medEntry?.[0]);
                                  const contra = medEntry?.[1]?.contraindications?.[0];
                                  return `${medCu?.medication_name || 'Medication'}: ${contra?.condition || 'caution'}`;
                                })()
                              : `${bucketContraCount} caution flag${bucketContraCount > 1 ? 's' : ''}`
                            }
                          </p>
                        )}
                        {data.summary && (
                          <p className="text-[10px] text-muted-foreground truncate">{data.summary}</p>
                        )}
                      </div>
                    ) : (
                      data.summary && (
                        <p className="text-[10px] text-muted-foreground truncate mt-0.5 leading-tight">
                          {data.summary}
                        </p>
                      )
                    )}
                  </div>

                  {/* Safety badge replaces normal status indicator when alert exists */}
                  {bucketHasSafetyIssue && !isComplete ? (
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                      bucketAllergyCount > 0 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                    }`} data-testid="safety-badge">
                      {bucketAllergyCount > 0 ? 'ALLERGY' : 'CAUTION'}
                    </span>
                  ) : (
                    <StatusIndicator status={data.status} pending={data.pending} />
                  )}
                  <ChevronRight className={`w-3.5 h-3.5 text-muted-foreground/50 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                </div>
              </button>

              {/* Mini Panels */}
              {isOpen && bucket.key === 'prescriptions' && (
                <MiniPanelPrescriptions
                  enc={enc} outputs={outputs} encounterId={encounterId}
                  fetchEncounter={fetchEncounter} setEncounter={setEncounter}
                  expandedMed={expandedMed} setExpandedMed={setExpandedMed}
                  medEdits={medEdits} setMedEdits={setMedEdits}
                  onClose={closeMiniPanel}
                  onOpenFull={() => { closeMiniPanel(); navigate(`/finalize/${encounterId}`); }}
                />
              )}
              {isOpen && bucket.key === 'labs' && (
                <MiniPanelLabs
                  enc={enc} outputs={outputs} encounterId={encounterId}
                  fetchEncounter={fetchEncounter}
                  onClose={closeMiniPanel}
                  onOpenFull={() => { closeMiniPanel(); navigate(`/finalize/${encounterId}`); }}
                />
              )}
              {/* ACO / Quality — chip strip ALWAYS inline, no click required */}
              {bucket.key === 'aco' && (
                <div className="px-3 pb-2 pt-1">
                  <QualityChipStrip
                    encounterId={encounterId}
                    patientId={enc?.patient_id}
                    context="live_visit"
                    density="compact"
                    onChipClick={(chip) => { setDrawerChip(chip); }}
                    onStateUpdate={(s) => setChipSummary(s.summary)}
                    className="flex-wrap gap-1"
                  />
                  {isOpen && (
                    <button
                      onClick={() => { closeMiniPanel(); navigate(`/finalize/${encounterId}`); }}
                      className="text-[9px] text-primary hover:text-primary/80 mt-1.5 block">
                      Review all at finalization →
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Quality chip drawer */}
        {drawerChip && (
          <QualityChipStripDrawer
            chip={drawerChip}
            encounterId={encounterId}
            onClose={() => setDrawerChip(null)}
            onActionTaken={() => { setDrawerChip(null); }}
          />
        )}

        {/* Divider */}
        <div className="border-t border-border/50 my-1" />

        {/* Finalization Gateway */}
        {enc?.status !== 'finalized' ? (
          <Button
            className="w-full text-xs gap-1.5 h-9"
            onClick={() => navigate(`/finalize/${encounterId}`)}
            data-testid="open-finalization-btn"
          >
            <ArrowRight className="w-3.5 h-3.5" />
            Open Finalization
          </Button>
        ) : (
          <div className="text-center py-2" data-testid="staging-finalized">
            <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 gap-1">
              <Check className="w-3 h-3" /> Finalized
            </Badge>
            <Button variant="link" size="sm" className="text-xs block mx-auto mt-1"
              onClick={() => navigate(`/note/${encounterId}`)}>
              View Note
            </Button>
          </div>
        )}

        {/* Empty state */}
        {!outputs?.billing_suggestion?.primary_code && !outputs?.soap_note?.confidence_score && (
          <div className="text-center py-4">
            <Sparkles className="w-5 h-5 mx-auto mb-1.5 text-muted-foreground/30" />
            <p className="text-[10px] text-muted-foreground">Generate note to populate staging</p>
          </div>
        )}
      </div>
    </ScrollArea>
  );
}


function StatusIndicator({ status, pending }) {
  if (status === 'complete') {
    return <Check className="w-3.5 h-3.5 text-emerald-500" />;
  }
  if (status === 'partial') {
    return (
      <span className="flex items-center gap-0.5">
        <Clock className="w-3 h-3 text-amber-500" />
        {pending > 0 && <span className="text-[9px] text-amber-600 tabular-nums">{pending}</span>}
      </span>
    );
  }
  if (status === 'attention') {
    return <AlertCircle className="w-3.5 h-3.5 text-amber-500" />;
  }
  return null;
}


function extractBucketData(enc, outputs, qualityOverview) {
  const billing = outputs?.billing_suggestion;
  const chartUpdates = outputs?.chart_updates || [];
  const suggestedActions = enc?.suggested_actions || [];

  // ── Coding — richer summary ──
  const codingCodes = [billing?.primary_code, ...(billing?.diagnosis_codes || [])].filter(Boolean);
  const codingApproved = billing?.approval_status === 'approved' || billing?.approval_status === 'approve';
  const coding = {
    count: codingCodes.length,
    summary: codingCodes.length > 0
      ? `${billing?.primary_code || '—'}${billing?.diagnosis_codes?.length ? ` + ${billing.diagnosis_codes.length} Dx` : ''}${codingApproved ? ' ✓' : ''}`
      : null,
    status: codingCodes.length === 0 ? null : codingApproved ? 'complete' : 'attention',
    pending: codingApproved ? 0 : (codingCodes.length > 0 ? 1 : 0),
  };

  // ── Prescriptions — action-type breakdown ──
  const meds = chartUpdates.filter(cu => cu.update_type?.startsWith('medication'));
  const medsPending = meds.filter(cu => !cu.disposition || cu.disposition === 'unreviewed').length;
  const medsQueued = meds.filter(cu => cu.disposition === 'queued').length;
  const medsSent = meds.filter(cu => cu.disposition === 'approve' || cu.disposition === 'sent').length;
  // Fix #4: "reviewed but not linked" is NOT complete
  const medsExecuted = meds.filter(cu => cu.disposition && cu.disposition !== 'unreviewed' && cu.disposition !== 'reject');
  const medsUnlinked = medsExecuted.filter(cu => !cu.linked_diagnosis && !cu.medication_linked_dx).length;
  const medsFullyComplete = medsPending === 0 && medsUnlinked === 0;
  const prescriptions = {
    count: meds.length,
    summary: meds.length > 0
      ? [
          medsPending > 0 && `${medsPending} to review`,
          medsSent > 0 && `${medsSent} sent`,
          medsQueued > 0 && `${medsQueued} queued`,
          medsUnlinked > 0 && `${medsUnlinked} unlinked`,
        ].filter(Boolean).join(', ') || 'All handled ✓'
      : null,
    status: meds.length === 0 ? null : !medsFullyComplete ? 'partial' : 'complete',
    pending: medsPending + medsUnlinked,
  };

  // ── Labs ──
  const labActions = suggestedActions.filter(a =>
    a.action_type === 'screening' || a.action_type === 'lab_order' ||
    (a.action_name || '').toLowerCase().includes('lab') || (a.action_name || '').toLowerCase().includes('screen')
  );
  const labsPending = labActions.filter(a => a.status === 'pending').length;
  // Fix #4: executed but unlinked labs are NOT complete
  const labsExecuted = labActions.filter(a => a.status !== 'pending' && a.status !== 'dismissed');
  const labsUnlinked = labsExecuted.filter(a => !a.linked_diagnosis).length;
  const labsFullyComplete = labsPending === 0 && labsUnlinked === 0;
  const labs = {
    count: labActions.length,
    summary: labActions.length > 0
      ? [
          labsPending > 0 && `${labsPending} to confirm`,
          labsUnlinked > 0 && `${labsUnlinked} unlinked`,
        ].filter(Boolean).join(', ') || 'All confirmed ✓'
      : null,
    status: labActions.length === 0 ? null : !labsFullyComplete ? 'partial' : 'complete',
    pending: labsPending + labsUnlinked,
  };

  // ── Actions (renamed from Tasks) ──
  const taskActions = suggestedActions.filter(a =>
    ['task','injection','form','referral','follow_up','screening'].includes(a.action_type)
  );
  const tasksPending = taskActions.filter(a => a.status === 'pending').length;
  const actions = {
    count: taskActions.length,
    summary: taskActions.length > 0
      ? tasksPending > 0 ? `${tasksPending} to route` : 'All routed ✓'
      : null,
    status: taskActions.length === 0 ? null : tasksPending > 0 ? 'partial' : 'complete',
    pending: tasksPending,
  };

  // ── ACO / Quality ── (real-time chip state drives the count/summary)
  const mustAddress = qualityOverview?.header?.must_address_count || 0;
  const totalGaps = qualityOverview?.header?.total_gap_count || 0;
  const preventive = chartUpdates.filter(cu => cu.update_type === 'preventive_update' || cu.update_type === 'preventive_prompt');
  const preventivePending = preventive.filter(cu => !cu.disposition || cu.disposition === 'unreviewed').length;
  const aco = {
    count: totalGaps + preventive.length,
    summary: mustAddress > 0 ? `${mustAddress} must-address` : preventive.length > 0 ? `${preventive.length} proposals` : totalGaps > 0 ? `${totalGaps} gaps` : null,
    status: mustAddress > 0 || preventivePending > 0 ? 'attention' : (totalGaps > 0 || preventive.length > 0) ? 'complete' : null,
    pending: mustAddress + preventivePending,
  };

  const _totalPending = (coding.pending || 0) + (prescriptions.pending || 0) +
    (labs.pending || 0) + (actions.pending || 0) + (aco.pending || 0);

  // Fix #1: "Ready" means truly safe to sign — all linked, all reviewed
  const _allLinked = medsUnlinked === 0 && labsUnlinked === 0;

  return { coding, prescriptions, labs, actions, aco, _totalPending, _allLinked };
}
